import type { TextFieldPropOnChange, TextFieldProps } from '@consta/uikit/TextField'
import { TextField } from '@consta/uikit/TextField'
import { useForkRef } from '@consta/uikit/useForkRef'
import { isDefined, preventDefault } from 'common/helpers'
import type { KeyboardEventHandler, MouseEventHandler, WheelEventHandler } from 'react'
import { forwardRef, useEffect, useRef } from 'react'

import { useEventListener, useSyncState } from '../../hooks'
import type { Nullable, Prettify } from '../../types'
import type { NumericMode } from './helpers'
import { isSyncingSignificantZeros, MODE_VALIDATORS } from './helpers'

export type NumericInputProps = Prettify<
  Omit<TextFieldProps<'number'>, 'type' | 'min' | 'max' | 'state' | 'value'> & {
    value?: Nullable<string | number>
    onSetDefault?: () => Nullable<string>
    mode?: NumericMode
  }
>

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(function NumericInput(
  {
    value: externalValue,
    onSetDefault,
    onChange,
    onWheel,
    onKeyDown,
    onClear,
    mode = 'float',
    ...rest
  },
  outerRef,
) {
  const innerRef = useRef<HTMLInputElement>(null)

  const [value, setValue] = useSyncState(externalValue, false, isSyncingSignificantZeros)

  const { isComplete, isInProgress } = MODE_VALIDATORS[mode]

  const handleChange: TextFieldPropOnChange = (newValue, args) => {
    const isValueDefined = isDefined(newValue)
    if (!isValueDefined) {
      onChange?.(null, args)
      setValue(null)
    }
    if (isValueDefined && isInProgress(newValue)) {
      setValue(newValue.replace(',', '.'))
    }
    if (isValueDefined && isComplete(newValue)) {
      onChange?.(newValue.replace(',', '.'), args)
      setValue(newValue)
    }
  }

  useEffect(() => {
    if (isDefined(value) || !isDefined(onSetDefault)) return

    setValue(onSetDefault())
  }, [onSetDefault, value])

  const handleClear: MouseEventHandler<HTMLButtonElement> = event => {
    onClear?.(event)
    handleChange(null, {
      e: event,
      id: rest.id,
      name: rest.name,
    })
  }

  const handleWheel: WheelEventHandler<HTMLDivElement> = event => {
    if (rest.disabled) {
      return
    }

    onWheel?.(event)
    if (value != null && typeof rest.step === 'number') {
      const precision = getPrecision(rest.step)
      const newValue = Number(value) - (rest.step * event.deltaY) / 100
      handleChange(newValue.toFixed(precision), {
        e: event,
        id: rest.id,
        name: rest.name,
      })
    }
  }

  const handleKeyDown: KeyboardEventHandler = event => {
    onKeyDown?.(event)
    if (
      value != null &&
      typeof rest.step === 'number' &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    ) {
      event.preventDefault()
      let delta = 0
      switch (event.key) {
        case 'ArrowDown':
          delta = -rest.step
          break
        case 'ArrowUp': {
          delta = rest.step
          break
        }
      }

      const precision = getPrecision(rest.step)
      const newValue = Number(value) + delta
      handleChange(newValue.toFixed(precision), {
        e: event,
        id: rest.id,
        name: rest.name,
      })
    }
  }

  useEventListener('wheel', preventDefault, innerRef, {
    passive: false,
  })

  return (
    <TextField
      ref={useForkRef<HTMLInputElement>([innerRef, outerRef])}
      type="text"
      value={value?.toString() ?? null}
      autoComplete="off"
      aria-autocomplete="none"
      onChange={handleChange}
      onWheel={handleWheel}
      onKeyDown={handleKeyDown}
      onClear={handleClear}
      {...rest}
    />
  )
})

function getPrecision(step: number) {
  if (!isFinite(step)) return 0

  let exponent = 1
  let precision = 0
  while (Math.round(step * exponent) / exponent !== step) {
    exponent *= 10
    precision++
  }
  return precision
}
